import React, { useState, useEffect, useRef } from "react";
import { useSwipeable } from "react-swipeable";
import { IoMdArrowDropright, IoMdArrowDropleft } from "react-icons/io";

interface CarouselProps {
  children: React.ReactNode[];
  className?: string;
}

const Carousel: React.FC<CarouselProps> = ({ children, className = "" }) => {
  const totalSlides = React.Children.count(children);
  const [currentIndex, setCurrentIndex] = useState(1); // Start at first clone
  const [isTransitioning, setIsTransitioning] = useState(false);
  const carouselRef = useRef<HTMLDivElement>(null);

  const slides = [
    React.Children.toArray(children)[totalSlides - 1], // Clone last slide to the start
    ...React.Children.toArray(children),
    React.Children.toArray(children)[0], // Clone first slide to the end
  ];

  const nextSlide = () => {
    if (!isTransitioning) {
      setIsTransitioning(true);
      setCurrentIndex((prevIndex) => prevIndex + 1);
    }
  };

  const prevSlide = () => {
    if (!isTransitioning) {
      setIsTransitioning(true);
      setCurrentIndex((prevIndex) => prevIndex - 1);
    }
  };

  const handlers = useSwipeable({
    onSwipedLeft: nextSlide,
    onSwipedRight: prevSlide,
    preventScrollOnSwipe: true,
    trackMouse: true,
  });

  useEffect(() => {
    const handleTransitionEnd = () => {
      setIsTransitioning(false);

      if (currentIndex === 0) {
        setCurrentIndex(totalSlides); // Jump to the last real slide
      } else if (currentIndex === totalSlides + 1) {
        setCurrentIndex(1); // Jump to the first real slide
      }
    };

    const carousel = carouselRef.current;
    if (carousel) {
      carousel.addEventListener("transitionend", handleTransitionEnd);
    }

    return () => {
      if (carousel) {
        carousel.removeEventListener("transitionend", handleTransitionEnd);
      }
    };
  }, [currentIndex, totalSlides]);

  return (
    <div {...handlers} className={`relative overflow-hidden ${className}`}>
      <div
        ref={carouselRef}
        className={`flex transition-transform ease-out duration-500 ${
          isTransitioning ? "" : "transition-none"
        }`}
        style={{ transform: `translateX(-${currentIndex * 100}%)` }}
      >
        {slides.map((child, index) => (
          <div key={index} className="w-full flex-shrink-0">
            {child}
          </div>
        ))}
      </div>

      {/* Navigation Buttons */}

      {/* Mobile Arrows */}
      <div className="absolute inset-0 flex justify-between items-center">
        <button onClick={prevSlide} className="p-2">
          <IoMdArrowDropleft size={32} className="text-gray-600" />
        </button>
        <button onClick={nextSlide} className="p-2">
          <IoMdArrowDropright size={32} className="text-gray-600" />
        </button>
      </div>

      {/* Indicator Dots */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
        {React.Children.map(children, (_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index + 1)} // Offset for cloned slides
            className={`h-2 w-2 rounded-full ${
              currentIndex === index + 1 ? "bg-black" : "bg-gray-400"
            }`}
          ></button>
        ))}
      </div>
    </div>
  );
};

export default Carousel;
